import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { reduxForm, formValueSelector, Field } from 'redux-form';
import styled from 'styled-components';
import { Container, Row, Col } from 'styled-bootstrap-grid';

import { useTranslations } from '@lang';

import Paragraph from '@common/components/Paragraph';
import { Button } from '@common/components/Button';
import { StickyBarContent } from '@common/components/StickyBar';
import Portal from '@common/components/Portal';

import { Extras } from '@common/components/Extras';
import { Heading4, Title1 } from '@common/components/Typography';
import { Subheading } from '@common/components/BookingForms';
import SelectionGroups from '@features/Customise/SelectionGroups';

import { validate } from '@features/Customise/customiseValidator';

const StyledContainer = styled(Container)`
  max-width: 968px;
  margin-left: 0;
`;

const SectionTitleWrapper = styled.div`
  margin: 30px 0;
`;

const CustomizeStep = ({
  spaEntity,
  stickyBarId,
  handleSubmit,
  onSubmit,
  selectedGuests,
  selectedArrivalTime,
  selectedExtras = {},
  freeExtras = [],
  paidExtras = [],
  selectedGroups = {},
  handleInvalidConfig,
}) => {
  const { t } = useTranslations();
  useEffect(() => {
    if (!selectedGuests || !selectedArrivalTime) {
      handleInvalidConfig();
    }
  }, [selectedGuests, selectedArrivalTime]);
  const getCalculatedPrice = () => {
    const numberOfGuests = selectedGuests.length;
    const baseAmount = selectedArrivalTime?.price?.amount || 0;
    let extrasAmount = 0;

    if (selectedExtras) {
      extrasAmount = Object.values(selectedExtras)
        .map((extra) =>
          extra ? extra.reduce((total, current) => total + current.price.amount, 0) : 0
        )
        .reduce((total, current) => total + current, 0);
    }

    const calculatedAmount = baseAmount * numberOfGuests + extrasAmount;

    return {
      ...selectedArrivalTime.price,
      amount: calculatedAmount,
    };
  };

  const getCalculatedDiscount = () => {
    if (!selectedArrivalTime.discount) {
      return undefined;
    }
    const numberOfGuests = selectedGuests.length;
    const baseAmount = selectedArrivalTime.discount.discountedPrice.amount;
    let extrasAmount = 0;

    if (selectedExtras) {
      extrasAmount = Object.values(selectedExtras)
        .map((extra) =>
          extra ? extra.reduce((total, current) => total + current.price.amount, 0) : 0
        )
        .reduce((total, current) => total + current, 0);
    }

    const calculatedAmount = baseAmount * numberOfGuests + extrasAmount;

    return {
      discountedPrice: {
        ...selectedArrivalTime.discount.discountedPrice,
        amount: calculatedAmount,
      },
    };
  };

  return selectedGuests ? (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <StyledContainer fluid>
        <Row>
          <Col col>
            <Heading4 tag="h1">{t('treatment.customiseYourExperience')}</Heading4>
            {spaEntity.selectionGroups?.length > 0 && (
              <div>
                <SectionTitleWrapper>
                  <Title1 tag="h2">{t('treatment.includedOptions')}</Title1>
                  <Paragraph>{t('treatment.optionsIncludedInSpaDay')}</Paragraph>
                </SectionTitleWrapper>
                <Field
                  name="selectionGroups"
                  component={SelectionGroups}
                  selectionGroups={spaEntity.selectionGroups}
                  selectedGuests={selectedGuests}
                  selectedGroups={selectedGroups}
                  paidExtras={paidExtras}
                  freeExtras={freeExtras}
                />
              </div>
            )}
          </Col>
        </Row>
        {freeExtras?.length > 0 && (
          <Row>
            <Col col>
              <Subheading>{t('treatment.freeExtrasHeader')}</Subheading>
              <Container fluid>
                <Extras
                  extras={freeExtras}
                  selectedGuests={selectedGuests}
                  selectedExtras={selectedExtras}
                  showPrice
                />
              </Container>
            </Col>
          </Row>
        )}
        {paidExtras?.length > 0 && (
          <Row>
            <Col col>
              <Title1 tag="h2">{t('treatment.extrasAndAdditions')}</Title1>
              <Container fluid>
                <Extras
                  extras={paidExtras}
                  selectedGuests={selectedGuests}
                  selectedExtras={selectedExtras}
                  showPrice
                />
              </Container>
            </Col>
          </Row>
        )}
      </StyledContainer>
      <Portal selector={`#${stickyBarId}`}>
        <StickyBarContent
          name={spaEntity.name}
          displayedPrice={getCalculatedPrice()}
          discountedPrice={getCalculatedDiscount()}
          button={
            <Button isPrimary type="submit" onClick={handleSubmit(onSubmit)}>
              {t('booking.continue')}
            </Button>
          }
        />
      </Portal>
    </form>
  ) : null;
};

CustomizeStep.propTypes = {
  spaEntity: PropTypes.shape({
    name: PropTypes.string,
    extras: PropTypes.array,
    selectionGroups: PropTypes.array,
  }).isRequired,
  guest: PropTypes.shape({}),
  selectedArrivalTime: PropTypes.shape({
    price: PropTypes.object,
    date: PropTypes.string,
    time: PropTypes.string,
  }).isRequired,
  selectedGuests: PropTypes.arrayOf(PropTypes.object).isRequired,
  selectedExtras: PropTypes.arrayOf(PropTypes.object).isRequired,
  stickyBarId: PropTypes.string.isRequired,
  onSubmit: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  handleInvalidConfig: PropTypes.func.isRequired,
  selectedGroups: PropTypes.array,
  paidExtras: PropTypes.array,
  freeExtras: PropTypes.array,
};

const selector = formValueSelector('spaEntityConfigurationForm');

const mapStateToProps = (state) => ({
  selectedArrivalTime: selector(state, 'arrivalTime'),
  selectedGuests: selector(state, 'guests'),
  selectedExtras: selector(state, 'extras'),
  selectedGroups: selector(state, 'selectionGroups'),
});

const CustomizeSpaEntityReduxForm = reduxForm({
  form: 'spaEntityConfigurationForm',
  destroyOnUnmount: false,
  forceUnregisterOnUnmount: true,
  validate,
})(CustomizeStep);

export default connect(mapStateToProps)(CustomizeSpaEntityReduxForm);
